/**
 * Карточка сданного отчёта в ленте дня (задача 71 §5.2; плашка расхождений — 22 §4.2).
 *
 * Состояния:
 *  - `signed` → бейдж `○ ждёт`, развёрнутая карточка: при расхождении — именованная
 *    плашка (caution) сразу после имени/должности, затем записи + подвал + кнопки.
 *  - `approved` → бейдж `подтв.`, свёрнутая строка (Таб / Зад) + «Отменить».
 *
 * Расхождение несёт **плашка** (старый одиночный бейдж `△ расхождение` убран).
 * Два класса (правило перекрытия — презентационное, §4.2):
 *  - `absence_event_type` (работал в день отсутствия) → показываем ТОЛЬКО строку
 *    отсутствия, строку «больше/меньше нормы» подавляем (норма в этот день = 0).
 *  - иначе `declared_off_norm` → строка «больше/меньше нормы» по знаку
 *    `declared_total_hours_half` vs `shift_norm_hours_half`.
 * «Σзадачи vs табель» — нейтральные числа, без ярлыка (§2.1). Мастер виден
 * маркером «ВЫ» (собственные часы руководителя, подписанные через self-flow §1.1).
 */
import { AlertTriangle, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PersonNameLink } from "@/components/people/PersonNameLink";
import { cn } from "@/lib/utils";
import { formatHoursHalf } from "@/lib/timesheet";
import type { DayReportCard, DayReportEntry } from "@/api/approval";

/**
 * Строки плашки расхождений по правилу перекрытия (§4.2): в день зануляющего
 * отсутствия — только строка отсутствия; иначе off-norm по знаку. Пустой список →
 * плашки нет.
 */
function discrepancyRows(
  card: DayReportCard,
  t: (key: string, options?: { name: string }) => string,
): string[] {
  if (card.absence_event_type != null) {
    return [t("timeApproval.discAbsence", { name: card.absence_event_type.name })];
  }
  if (card.declared_off_norm) {
    const over = card.declared_total_hours_half > card.shift_norm_hours_half;
    return [t(over ? "timeApproval.discOverNorm" : "timeApproval.discUnderNorm")];
  }
  return [];
}

interface ReportCardProps {
  card: DayReportCard;
  isSelf: boolean;
  pending: boolean;
  onApprove: () => void;
  onUnapprove: () => void;
  onEdit: () => void;
  /**
   * Открыть карточку дня сотрудника в едином экране (задача 82 §1.2, «баг №1»):
   * клик по плашке (аватар сотрудника) ведёт в тот же `EntryDock` →
   * `TimesheetDayEditor`, что и «✎ Править». Профиль `/people/$id` остаётся
   * отдельной ссылкой на имени (`PersonNameLink`, 78 §2.2) — не дублируем её.
   */
  onOpenDay: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

export function ReportCard({
  card,
  isSelf,
  pending,
  onApprove,
  onUnapprove,
  onEdit,
  onOpenDay,
}: ReportCardProps): React.ReactElement {
  const { t } = useTranslation();
  if (card.is_absence_marker) {
    return <AbsenceMarkerCard card={card} />;
  }
  const approved = card.status === "approved";
  const offNorm = card.declared_off_norm;
  const rows = discrepancyRows(card, t);
  const hasDiscrepancy = rows.length > 0;

  return (
    <div
      data-testid={`approval-card-${card.user.id}`}
      data-status={card.status}
      data-off-norm={offNorm ? "true" : "false"}
      className={cn(
        "flex flex-col gap-3 rounded-[var(--radius-card)] border bg-[var(--bg-panel-0)] p-3",
      )}
      style={{
        borderColor:
          !approved && hasDiscrepancy ? "var(--tone-caution-border)" : "var(--rule-line)",
      }}
    >
      <CardHeader card={card} isSelf={isSelf} approved={approved} onOpenDay={onOpenDay} />

      {approved ? (
        <CompactBody card={card} pending={pending} onEdit={onEdit} onUnapprove={onUnapprove} />
      ) : (
        <>
          {hasDiscrepancy && <DiscrepancyPlate rows={rows} />}
          <EntryList entries={card.entries} />
          <CardFooter card={card} offNorm={offNorm} />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onEdit}>
              {`✎ ${t("timeApproval.edit")}`}
            </Button>
            <Button
              className="flex-1"
              disabled={pending}
              data-testid={`approval-approve-${card.user.id}`}
              onClick={onApprove}
            >
              {pending ? t("timeApproval.approving") : t("timeApproval.approve")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function CardHeader({
  card,
  isSelf,
  approved,
  onOpenDay,
}: {
  card: DayReportCard;
  isSelf: boolean;
  approved: boolean;
  onOpenDay: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5">
      {/* Клик по плашке (аватару) → карточка дня сотрудника в едином экране
          (задача 82 §1.2). Имя рядом ведёт в профиль `/people/$id` — две разные
          цели, аватар не вложен в ссылку (валидный DOM). */}
      <button
        type="button"
        onClick={onOpenDay}
        data-testid={`approval-open-day-${card.user.id}`}
        aria-label={t("timeApproval.openDay", { name: card.user.display_name })}
        className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-chip)] font-mono text-xs font-semibold uppercase transition-colors hover:brightness-95"
        style={{ background: "var(--bg-panel-1)", color: "var(--fg-2)" }}
      >
        {initials(card.user.display_name)}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <PersonNameLink
            id={card.user.id}
            name={card.user.display_name}
            fullName={card.user.full_name}
            className="truncate text-sm font-semibold text-[var(--fg-1)] hover:underline"
          />
          {isSelf && (
            <span
              className="rounded-[var(--radius-chip)] px-1 py-0.5 font-mono text-[10px] font-semibold"
              style={{ background: "var(--accent-tech)", color: "#fff" }}
            >
              {t("timeApproval.you")}
            </span>
          )}
        </div>
        {card.user.position && (
          <div className="truncate font-mono text-[11px] text-[var(--fg-3)]">
            {card.user.position}
          </div>
        )}
      </div>
      {approved ? (
        <Badge tone="success" emphasis="soft">
          {t("timeApproval.badgeApproved")}
        </Badge>
      ) : (
        <Badge tone="caution" emphasis="soft">
          {t("timeApproval.badgeWaiting")}
        </Badge>
      )}
    </div>
  );
}

/**
 * Карточка-пометка отсутствия (§78/1.2): субъект целиком отсутствует в день
 * (целодневной тип группы `absence`) и табель не сдавал — остаётся в ленте
 * **нередактируемой** пометкой, а не исчезает. Та же нотация, что у свёрнутой
 * карточки статуса (тот же шелл/шапка), но без записей и кнопок: бейдж
 * `info`-тона «отсутствует» в шапке + вид/причина строкой ниже.
 */
function AbsenceMarkerCard({ card }: { card: DayReportCard }): React.ReactElement {
  const { t } = useTranslation();
  const eventType = card.absence_event_type;
  return (
    <div
      data-testid={`approval-card-${card.user.id}`}
      data-absence-marker="true"
      data-absence-event-type={eventType?.seed_key ?? ""}
      className="flex flex-col gap-2.5 rounded-[var(--radius-card)] border border-[var(--rule-line)] bg-[var(--bg-panel-0)] p-3"
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-chip)] font-mono text-xs font-semibold uppercase"
          style={{ background: "var(--bg-panel-1)", color: "var(--fg-3)" }}
          aria-hidden="true"
        >
          {initials(card.user.display_name)}
        </span>
        <div className="min-w-0 flex-1">
          <PersonNameLink
            id={card.user.id}
            name={card.user.display_name}
            fullName={card.user.full_name}
            className="block truncate text-sm font-semibold text-[var(--fg-1)] hover:underline"
          />
          {card.user.position && (
            <div className="truncate font-mono text-[11px] text-[var(--fg-3)]">
              {card.user.position}
            </div>
          )}
        </div>
        <Badge tone="info" emphasis="soft">
          {t("timeApproval.markBadge")}
        </Badge>
      </div>

      <div
        data-testid="approval-absence-mark"
        className="tone-info flex items-baseline gap-2 rounded-[var(--radius-chip)] px-2.5 py-2 text-sm"
      >
        <span className="font-condensed font-semibold uppercase tracking-[0.04em]">
          {eventType?.name ?? t("timeApproval.markBadge")}
        </span>
        {card.absence_reason && (
          <span className="min-w-0 flex-1 truncate text-[var(--fg-2)]">{card.absence_reason}</span>
        )}
      </div>
    </div>
  );
}

function DiscrepancyPlate({ rows }: { rows: string[] }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      data-testid="approval-discrepancy"
      className="flex flex-col gap-1 rounded-[var(--radius-chip)] px-2.5 py-2"
      style={{
        background: "var(--tone-caution-bg)",
        border: "1px solid var(--tone-caution-border)",
        color: "var(--tone-caution-fg)",
      }}
    >
      <span className="font-condensed text-[11px] font-semibold uppercase tracking-[0.04em]">
        {t("timeApproval.discTitle")}
      </span>
      {rows.map((row) => (
        <span key={row} className="flex items-baseline gap-1.5 text-xs">
          <span aria-hidden="true">△</span>
          <span>{row}</span>
        </span>
      ))}
    </div>
  );
}

function EntryList({ entries }: { entries: DayReportEntry[] }): React.ReactElement {
  return (
    <div className="flex flex-col">
      {entries.map((e, i) => (
        <div
          key={e.id}
          className={cn(
            "flex items-baseline gap-2 py-1.5",
            i > 0 && "border-t border-[var(--rule-faint)]",
          )}
        >
          {showElementMissingMark(e) ? (
            <ElementMissingMark entryId={e.id} />
          ) : (
            <span
              data-testid={`approval-element-gutter-${e.id}`}
              className="size-3.5 shrink-0"
              aria-hidden="true"
            />
          )}
          <span className="min-w-0 flex-1 leading-snug">
            <span className="block truncate text-sm text-[var(--fg-1)]">{workTypeLabel(e)}</span>
            {entryProjectLabel(e) && (
              <span className="mt-0.5 block truncate font-mono text-[11px] tabular-nums text-[var(--fg-3)]">
                {entryProjectLabel(e)}
              </span>
            )}
          </span>
          <span className="shrink-0 self-start font-mono text-sm font-semibold tabular-nums text-[var(--fg-1)]">
            {formatHoursHalf(e.hours_half)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Пометка «требует внимания» уместна только когда элемент сборки *применим*, а
 * именно — у записи распознан проект из справочника (`e.project`), но элемент не
 * выбран (задача 89 §4.3, решение владельца). У free-text-записей
 * (`project_code_raw` без `e.project`) и записей вовсе без проекта элемент
 * структурно неприменим, пометку не показываем — иначе она шумит на строках, где
 * выбирать нечего.
 */
function showElementMissingMark(e: DayReportEntry): boolean {
  return e.project != null && e.assembly_element == null;
}

/**
 * Пометка «требует внимания» на записи без элемента сборки (задача 89 §4.3):
 * сотрудник сдал отчёт по проекту из справочника, не указав элемент. Сигнал
 * руководителю, сдачу не блокирует. Тот же caution-маркер `AlertTriangle`, что в
 * аудите таймшитов (`TimesheetAuditTab`), но пояснение — `Popover` (не
 * `Tooltip`): карточка рендерится и в мобильной консоли M6, где hover на
 * тач-экране не срабатывает.
 */
function ElementMissingMark({ entryId }: { entryId: number }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`approval-element-missing-${entryId}`}
          aria-label={t("timeApproval.elementMissing.title")}
          className="mt-0.5 shrink-0 self-start text-[var(--tone-caution-fg)] transition-opacity hover:opacity-80"
        >
          <AlertTriangle className="size-3.5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <p className="text-sm font-semibold text-[var(--fg-1)]">
          {t("timeApproval.elementMissing.title")}
        </p>
        <p className="mt-1 text-sm text-[var(--fg-2)]">{t("timeApproval.elementMissing.body")}</p>
      </PopoverContent>
    </Popover>
  );
}

function workTypeLabel(e: DayReportEntry): string {
  return e.work_type?.name ?? e.work_type_text ?? "—";
}

/**
 * Код проекта записи (вторичная muted-строка), паритет с sibling-консолью
 * `TimesheetDayEditor.entryProjectCode` (§2.5). Проект из справочника →
 * «код · название» (вложенный `e.project.{code,name}` в feed-модели, не плоские
 * поля); free-text → `project_code_raw` как есть; оба NULL → null (строка не
 * рендерится).
 */
function entryProjectLabel(e: DayReportEntry): string | null {
  if (e.project) return `${e.project.code} · ${e.project.name}`;
  return e.project_code_raw ?? null;
}

function CardFooter({
  card,
  offNorm,
}: {
  card: DayReportCard;
  offNorm: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--rule-faint)] pt-2">
      <FootStat
        label={t("timeApproval.footTasks")}
        value={formatHoursHalf(card.sum_tasks_hours_half)}
      />
      <span className="flex-1" />
      <span
        className="inline-flex items-center gap-1.5 text-xs"
        style={{ color: offNorm ? "var(--tone-caution-fg)" : "var(--fg-3)" }}
      >
        <span className="font-condensed uppercase tracking-[0.04em]">
          {t("timeApproval.footDeclaredVsNorm")}
        </span>
        <span className="font-mono tabular-nums">
          {formatHoursHalf(card.declared_total_hours_half)} /{" "}
          {formatHoursHalf(card.shift_norm_hours_half)}
        </span>
      </span>
    </div>
  );
}

function FootStat({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-condensed text-xs uppercase tracking-[0.04em] text-[var(--fg-3)]">
        {label}
      </span>
      <span className="font-mono text-sm font-semibold tabular-nums text-[var(--fg-1)]">
        {value}
      </span>
    </span>
  );
}

function CompactBody({
  card,
  pending,
  onEdit,
  onUnapprove,
}: {
  card: DayReportCard;
  pending: boolean;
  onEdit: () => void;
  onUnapprove: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 font-mono text-[13px]">
      <span className="text-[var(--fg-2)]">
        {t("timeApproval.compactDeclared")}{" "}
        <b className="text-[var(--fg-1)]">{formatHoursHalf(card.declared_total_hours_half, "")}</b>
      </span>
      <span className="text-[var(--fg-4)]">/</span>
      <span className="text-[var(--fg-2)]">
        {t("timeApproval.compactTasks")}{" "}
        <b className="text-[var(--fg-1)]">{formatHoursHalf(card.sum_tasks_hours_half, "")}</b>
      </span>
      <span className="flex-1" />
      <Button
        variant="outline"
        size="sm"
        data-testid={`approval-edit-${card.user.id}`}
        onClick={onEdit}
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden />
        {t("timeApproval.edit")}
      </Button>
      <button
        type="button"
        data-testid={`approval-unapprove-${card.user.id}`}
        disabled={pending}
        onClick={onUnapprove}
        className="font-mono text-[11px] text-[var(--accent-tech)] underline-offset-2 hover:underline disabled:opacity-50"
      >
        {t("timeApproval.cancelApproval")}
      </button>
    </div>
  );
}
